import { useEffect } from 'react';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE, SESSION_MASTER_TOKEN_URL } from '../config/sessionAppData';
import {
  SALON_PAGE_SETUP_TOKEN_KEY,
  SALON_PAGE_SETUP_DATA_PREFIX,
} from '../config/salonApp';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import {
  franchiseAuthHeaders,
  appendFranchiseThemeParams,
} from '../../shared/franchiseFlowAuth';
import { syncKioskPoweredByFlag } from '../../shared/kioskPoweredBy';

function pageSetupUrl(token) {
  const base = `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup`;
  const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
  return `${base}?${qs.toString()}`;
}

async function resolvePageSetupToken(tokenHint) {
  if (tokenHint) return tokenHint;
  try {
    const res = await fetchWithRetry(`${SESSION_MASTER_TOKEN_URL}?_=${Date.now()}`, {
      timeoutMs: 12000,
      cache: 'no-store',
      credentials: 'include',
      headers: franchiseAuthHeaders(),
    }, { label: 'page-setup-master-token' });
    const data = await res.json().catch(() => ({}));
    if (res.ok) return data?.public_token || null;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return '161, 224, 244';
  const n = parseInt(hex.replace(/^#/, ''), 16);
  if (Number.isNaN(n)) return '161, 224, 244';
  return [n >> 16, (n >> 8) & 255, n & 255].join(', ');
}

function themeRoots() {
  const roots = [document.documentElement];
  try {
    document.querySelectorAll('.naturals-salon-lux, .fi-lux-theme').forEach((el) => {
      if (el) roots.push(el);
    });
  } catch (_) {
    /* ignore */
  }
  return roots;
}

function setThemeProperty(name, value) {
  if (!name || value == null || value === '') return;
  themeRoots().forEach((el) => {
    try {
      el.style.setProperty(name, value);
    } catch (_) {
      /* ignore */
    }
  });
}

function normalizeGradientAngle(angle, fallback = 90) {
  const parsedAngle = Number(angle);
  if (!Number.isFinite(parsedAngle)) return fallback;
  return ((parsedAngle % 360) + 360) % 360;
}

function buildBackgroundGradient(primaryColor, secondaryColor, angle) {
  const normalizedAngle = normalizeGradientAngle(angle);
  const isVerticalHalfBlend = Math.abs(normalizedAngle) < 0.01 || Math.abs(normalizedAngle - 180) < 0.01;

  if (isVerticalHalfBlend) {
    return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${primaryColor} 30%, ${secondaryColor} 70%, ${secondaryColor} 100%)`;
  }

  return `linear-gradient(${normalizedAngle}deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
}

/**
 * Applies highlight colors from external_fields.highlight (page-setup).
 * - primary → --skin-highlight (form accents / gender cards)
 * - selection → --selection-highlight (selected buttons / options)
 */
function applyHighlightFromExternalFields(highlight) {
  if (!highlight) return;
  const primary =
    typeof highlight === 'string'
      ? highlight
      : highlight && typeof highlight === 'object'
        ? highlight.primary || highlight.color || highlight.secondary
        : null;
  const selection =
    typeof highlight === 'string'
      ? highlight
      : highlight && typeof highlight === 'object'
        ? highlight.selection ||
          highlight.selection_highlight ||
          highlight.selectionHighlight ||
          highlight.selection_color ||
          primary
        : null;
  if (primary && typeof primary === 'string') {
    setThemeProperty('--skin-highlight', primary);
    setThemeProperty('--skin-highlight-rgb', hexToRgb(primary));
  }
  const selectionColor =
    selection && typeof selection === 'string' ? selection : primary;
  if (selectionColor && typeof selectionColor === 'string') {
    setThemeProperty('--selection-highlight', selectionColor);
    setThemeProperty('--selection-highlight-rgb', hexToRgb(selectionColor));
  }
}

/**
 * Applies typography/font colors from external_fields.fonts (page-setup).
 * Sets --skin-text-primary, --skin-text-secondary, --skin-text-muted.
 */
function applyFontsFromExternalFields(fonts) {
  if (!fonts || typeof fonts !== 'object') return;
  const primary = fonts.primary;
  const secondary = fonts.secondary;
  const muted = fonts.muted;
  if (primary) {
    setThemeProperty('--skin-text-primary', primary);
    // Mirror onto kiosk landing tokens so hub / passport pages inherit Theme & page setup.
    setThemeProperty('--kiosk-text-body', primary);
    setThemeProperty('--kiosk-text-card-text', primary);
    setThemeProperty('--kiosk-text-consent', primary);
    // Makeup/Looks pages use --text-* from CommonBackground.
    setThemeProperty('--text-primary', primary);
  }
  if (secondary) {
    setThemeProperty('--skin-text-secondary', secondary);
    setThemeProperty('--kiosk-text-title', secondary);
    setThemeProperty('--kiosk-text-subtitle', secondary);
    setThemeProperty('--kiosk-text-brand-tagline', secondary);
    setThemeProperty('--kiosk-text-card-title', secondary);
    setThemeProperty('--kiosk-text-footer', secondary);
    setThemeProperty('--text-secondary', secondary);
  }
  if (muted) {
    setThemeProperty('--skin-text-muted', muted);
    setThemeProperty('--text-muted', muted);
    if (!primary) {
      setThemeProperty('--kiosk-text-body', muted);
      setThemeProperty('--kiosk-text-card-text', muted);
      setThemeProperty('--kiosk-text-consent', muted);
    }
  }
}

/**
 * Applies CTA button CSS variables from external_fields.cta (page-setup).
 * Sets --cta-background, --cta-text-color, --cta-border-color.
 */
function applyCtaFromExternalFields(cta) {
  if (!cta || typeof cta !== 'object') return;
  const primary = cta.primary_color;
  const secondary = cta.secondary_color;
  const backgroundType = (cta.background_type || 'solid').toLowerCase();
  const angle = typeof cta.gradient_angle === 'number' ? cta.gradient_angle : 90;
  const textColor = cta.text_color;
  const borderColor = cta.border_color;

  if (primary) {
    setThemeProperty('--cta-primary', primary);
    setThemeProperty('--cta-primary-rgb', hexToRgb(primary));
  }
  if (secondary) {
    setThemeProperty('--cta-secondary', secondary);
    setThemeProperty('--cta-secondary-rgb', hexToRgb(secondary));
  }
  if (textColor) {
    setThemeProperty('--cta-text-color', textColor);
  }
  if (borderColor) {
    setThemeProperty('--cta-border-color', borderColor);
  }
  if (backgroundType === 'gradient' && primary && secondary) {
    const gradient = `linear-gradient(${angle}deg, ${primary} 0%, ${secondary} 100%)`;
    setThemeProperty('--cta-background', gradient);
    setThemeProperty('--kiosk-cta-bg', primary);
    setThemeProperty('--kiosk-cta-bg-image', gradient);
  } else if (primary || secondary) {
    const solid = primary || secondary;
    setThemeProperty('--cta-background', solid);
    setThemeProperty('--kiosk-cta-bg', solid);
    setThemeProperty('--kiosk-cta-bg-image', 'none');
  }
  if (textColor) {
    setThemeProperty('--kiosk-cta-color', textColor);
  }
  if (borderColor || primary) {
    setThemeProperty('--kiosk-cta-border', borderColor || primary);
  }
  const label = typeof cta.text === 'string' ? cta.text.trim() : '';
  if (label) {
    document.documentElement.dataset.pageSetupCtaText = label;
  }
}

/**
 * Applies card/text border color from external_fields.borders (page-setup).
 * Sets --skin-border-color.
 */
function applyBordersFromExternalFields(borders) {
  if (!borders) return;
  const color =
    typeof borders === 'string'
      ? borders
      : borders && typeof borders === 'object'
        ? borders.color || borders.primary
        : null;
  if (!color || typeof color !== 'string') return;
  setThemeProperty('--skin-border-color', color);
  setThemeProperty('--skin-border-color-rgb', hexToRgb(color));
}

/**
 * Applies page-bg CSS variables from API external_fields.
 * Supports page-setup shape: external_fields.background { primary_color, secondary_color, type: "solid" | "gradient", gradient_angle }.
 * Also supports legacy shape: external_fields.primary_color / secondary_color.
 */
export function applyPageBgFromExternalFields(externalFields) {
  const bg = externalFields?.background;
  const primaryColor = bg?.primary_color ?? externalFields?.primary_color;
  const secondaryColor = bg?.secondary_color ?? externalFields?.secondary_color;
  const type = (bg?.type || '').toLowerCase();
  const gradientAngle = normalizeGradientAngle(bg?.gradient_angle, 90);
  const hasAnyColor = primaryColor || secondaryColor;
  if (!hasAnyColor) return;

  const useGradient = type === 'gradient' && primaryColor && secondaryColor;
  if (useGradient) {
    const gradient = buildBackgroundGradient(primaryColor, secondaryColor, gradientAngle);
    setThemeProperty('--page-bg-background', gradient);
    setThemeProperty('--kiosk-page-bg', gradient);
    setThemeProperty('--page-bg-primary', primaryColor);
    setThemeProperty('--page-bg-secondary', secondaryColor);
    setThemeProperty('--page-bg-primary-rgb', hexToRgb(primaryColor));
    setThemeProperty('--page-bg-secondary-rgb', hexToRgb(secondaryColor));
  } else {
    const solidColor = primaryColor || secondaryColor;
    setThemeProperty('--page-bg-background', solidColor);
    setThemeProperty('--kiosk-page-bg', solidColor);
    setThemeProperty('--page-bg-primary', solidColor);
    setThemeProperty('--page-bg-secondary', solidColor);
    setThemeProperty('--page-bg-primary-rgb', hexToRgb(solidColor));
    setThemeProperty('--page-bg-secondary-rgb', hexToRgb(solidColor));
  }
}

export const PAGE_SETUP_STORAGE_KEY = SALON_PAGE_SETUP_TOKEN_KEY;
export const PAGE_SETUP_DATA_PREFIX = SALON_PAGE_SETUP_DATA_PREFIX;

export function getPageSetupDataKey(token) {
  return token ? PAGE_SETUP_DATA_PREFIX + String(token).trim() : null;
}

/** Persist page-setup result so it can be re-applied on refresh (e.g. from LandingPage after fetch). */
export function storePageSetupData(token, result) {
  const key = getPageSetupDataKey(token);
  if (!key || !result) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(result));
    sessionStorage.setItem(PAGE_SETUP_STORAGE_KEY, token);
  } catch (_) {}
}

/** Apply external_fields and top-level domain from page-setup response (theme, CTA, fonts, highlight). */
export function applyPageSetupResult(result) {
  if (!result) return;
  const externalFields = result?.external_fields ||
    result?.data?.external_fields ||
    (Array.isArray(result) && result[0]?.external_fields);
  if (externalFields) {
    applyPageBgFromExternalFields(externalFields);
    applyCtaFromExternalFields(externalFields?.cta);
    applyFontsFromExternalFields(externalFields?.fonts);
    applyBordersFromExternalFields(externalFields?.borders);
    applyHighlightFromExternalFields(externalFields?.highlight);
  }
  const domain = result?.domain ?? result?.data?.domain;
  if (domain && typeof domain === 'string') {
    document.documentElement.dataset.pageSetupDomain = domain;
  }
  if (Object.prototype.hasOwnProperty.call(result, 'powered_by_nyafaz')
    || Object.prototype.hasOwnProperty.call(result?.data || {}, 'powered_by_nyafaz')) {
    const enabled = result?.powered_by_nyafaz ?? result?.data?.powered_by_nyafaz;
    syncKioskPoweredByFlag(enabled !== false);
  }
}

/**
 * Fetch page-setup (session token or public master token) and apply bg / fonts / CTA
 * across salon + kiosk pages. Re-applies from sessionStorage on refresh.
 */
export function PageBackgroundSync() {
  const { token } = useSkinSession();

  useEffect(() => {
    let cancelled = false;
    let activeToken = token || null;

    const applyStored = (tok) => {
      const dataKey = getPageSetupDataKey(tok);
      try {
        const stored = dataKey ? sessionStorage.getItem(dataKey) : null;
        if (stored) applyPageSetupResult(JSON.parse(stored));
      } catch (_) {
        /* ignore */
      }
      return dataKey;
    };

    if (activeToken) applyStored(activeToken);

    const fetchAndApply = async () => {
      const tok = await resolvePageSetupToken(token);
      if (cancelled || !tok) return;
      activeToken = tok;
      const dataKey = applyStored(tok);
      try {
        const res = await fetchWithRetry(pageSetupUrl(tok), {
          timeoutMs: 20000,
          cache: 'no-store',
          credentials: 'include',
          headers: franchiseAuthHeaders(),
        }, { label: 'page-setup' });
        const result = res.ok ? await res.json() : null;
        if (cancelled || !result) return;
        applyPageSetupResult(result);
        try {
          sessionStorage.setItem(PAGE_SETUP_STORAGE_KEY, tok);
          if (dataKey) sessionStorage.setItem(dataKey, JSON.stringify(result));
        } catch (_) {
          /* ignore */
        }
      } catch (_) {
        /* ignore */
      }
    };

    void fetchAndApply();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || cancelled) return;
      void fetchAndApply();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token]);

  return null;
}
